import { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, StatCard } from '@/components/ui/Card';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { ApiError } from '@/lib/api/client';
import { ScheduleBadge } from './ScheduleBadge';
import { useNearExpiryReport } from './useNearExpiry';
import { StorePicker } from './StorePicker';
import { useHasMultipleStores } from './usePharmacyStores';
import { batchReasonLabel } from './BatchSelectionPanel';
import type { NearExpiryRow } from './types';

const money = (v: number | string | null | undefined) =>
  v == null || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (d: string) => new Date(d).toLocaleDateString('en-IN');

/** Soonest first gets the strongest colour: under a month is urgent. */
function DaysLeft({ days }: { days: number }) {
  const variant = days <= 30 ? 'danger' : days <= 60 ? 'caution' : 'warn';
  return <Badge variant={variant}>{days === 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`}</Badge>;
}

type Bucket = 'all' | '30' | '60';

/**
 * Pharmacy → ⏳ Near Expiry — stock at this branch that expires inside the
 * hospital's near-expiry window (Admin Panel → Pharmacy → Dispensing & Expiry),
 * soonest first, with its value, so it can be dispensed first, returned to the
 * vendor or moved before it becomes a write-off. Below it, the audit trail of
 * sales that used a later-expiry batch while an earlier one was available,
 * with the reason recorded at the time.
 *
 * Read-only: acting on a batch (a write-off, a correction) stays on the
 * Inventory tab's stock adjustment, which is where every stock movement is
 * already recorded.
 */
export default function NearExpiryTab() {
  const [selectionsDays, setSelectionsDays] = useState(30);
  const [search, setSearch] = useState('');
  const [bucket, setBucket] = useState<Bucket>('all');
  const [storeId, setStoreId] = useState<number | undefined>(undefined);
  const multipleStores = useHasMultipleStores();
  const query = useNearExpiryReport(selectionsDays, storeId);
  const report = query.data;

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (report?.rows ?? []).filter((r: NearExpiryRow) => {
      if (bucket !== 'all' && r.days_to_expiry > Number(bucket)) return false;
      if (!term) return true;
      return [r.generic_name, r.brand_name, r.batch_number].some((v) => (v || '').toLowerCase().includes(term));
    });
  }, [report, search, bucket]);

  if (query.isLoading) return <LoadingState />;
  if (!report) {
    return (
      <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">
        {query.error instanceof ApiError ? query.error.message : 'Could not load the near-expiry report.'}
      </div>
    );
  }

  const shown = rows.reduce(
    (acc, r) => ({ qty: acc.qty + r.quantity_available, mrp: acc.mrp + Number(r.value_mrp || 0), cost: acc.cost + Number(r.value_cost || 0) }),
    { qty: 0, mrp: 0, cost: 0 }
  );

  return (
    <div className="space-y-4">
      {multipleStores && (
        <div className="flex justify-end">
          <StorePicker value={storeId} onChange={setStoreId} />
        </div>
      )}
      <div className="grid gap-3 min-[700px]:grid-cols-4">
        <Stat label={`Batches expiring within ${report.near_expiry_days} days`} value={String(report.totals.batch_count)} />
        <Stat label="Units on the shelf" value={report.totals.quantity.toLocaleString('en-IN')} />
        <Stat label="Value at MRP" value={money(report.totals.value_mrp)} />
        <Stat label="Value at cost" value={money(report.totals.value_cost)} hint="Batches with no purchase price are left out" />
      </div>

      {report.expired.batch_count > 0 && (
        <div className="rounded-lg border border-danger-soft bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">
          <b>{report.expired.batch_count}</b> batch{report.expired.batch_count === 1 ? '' : 'es'} already expired with stock
          ({report.expired.quantity} units, {money(report.expired.value_mrp)} at MRP) — they cannot be dispensed. Write them
          off from the Inventory tab (Adjust → Expired write-off).
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>⏳ Near-expiry stock</CardTitle>
          <span className="text-[11.5px] text-ink-muted">Window: {report.near_expiry_days} days (Admin Panel → Pharmacy → Dispensing &amp; Expiry)</span>
        </CardHeader>
        <div className="mb-3 flex flex-wrap gap-2">
          <Input className="min-w-[200px] flex-1" placeholder="Search medicine or batch…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <NativeSelect className="w-auto" value={bucket} onChange={(e) => setBucket(e.target.value as Bucket)} aria-label="Expiring within">
            <option value="all">All in the window</option>
            <option value="30">30 days or less</option>
            <option value="60">60 days or less</option>
          </NativeSelect>
        </div>
        {rows.length === 0 ? (
          <EmptyState label={report.rows.length ? 'No batches match this filter' : `Nothing expires within ${report.near_expiry_days} days`} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {['Medicine', 'Batch', 'Expiry', 'Days left', 'Qty', 'MRP', 'Value (MRP)', 'Value (cost)'].map((h) => <TableHead key={h}>{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="flex items-center gap-1.5 font-semibold">
                        {r.generic_name} {r.strength}
                        {r.schedule !== 'OTC' && <ScheduleBadge schedule={r.schedule} />}
                      </div>
                      {r.brand_name && <div className="text-[11px] text-ink-muted">{r.brand_name}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.batch_number}
                      {multipleStores && !storeId && <div className="font-sans text-[11px] text-ink-muted">{r.store_name}</div>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{date(r.expiry_date)}</TableCell>
                    <TableCell><DaysLeft days={r.days_to_expiry} /></TableCell>
                    <TableCell className="tabular-nums">{r.quantity_available}</TableCell>
                    <TableCell className="tabular-nums">{money(r.mrp)}</TableCell>
                    <TableCell className="tabular-nums font-semibold">{money(r.value_mrp)}</TableCell>
                    <TableCell className="tabular-nums">{money(r.value_cost)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-semibold" colSpan={4}>Shown ({rows.length})</TableCell>
                  <TableCell className="tabular-nums font-semibold">{shown.qty}</TableCell>
                  <TableCell />
                  <TableCell className="tabular-nums font-semibold">{money(shown.mrp)}</TableCell>
                  <TableCell className="tabular-nums font-semibold">{money(shown.cost)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>↷ Later-expiry batches dispensed</CardTitle>
          <NativeSelect className="w-auto" value={selectionsDays} onChange={(e) => setSelectionsDays(Number(e.target.value))} aria-label="Period">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </NativeSelect>
        </CardHeader>
        <p className="-mt-2 mb-3 text-[11.5px] text-ink-muted">
          Sales that used a later-expiry batch while an earlier one could have covered the line, with the reason given.
        </p>
        {report.selections.length === 0 ? (
          <EmptyState label="None in this period" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {['When', 'Bill', 'Patient', 'Medicine', 'Batch (exp)', 'Qty', 'Reason', 'By'].map((h) => <TableHead key={h}>{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.selections.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="whitespace-nowrap text-xs text-ink-muted">{new Date(s.created_at).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {s.bill_number}
                      <div className="text-[10.5px] text-ink-muted">{s.sale_channel === 'outpatient' ? 'Walk-in counter' : 'Visit dispensing'}</div>
                    </TableCell>
                    <TableCell>{s.patient_name ?? '—'}{s.uhid && <div className="text-[10.5px] text-ink-muted">{s.uhid}</div>}</TableCell>
                    <TableCell>{s.description}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs">{s.batch_number} ({s.expiry_date ? date(s.expiry_date) : '—'})</TableCell>
                    <TableCell className="tabular-nums">{s.quantity}</TableCell>
                    <TableCell>
                      {batchReasonLabel(s.batch_selection_reason, report.batch_selection_reasons)}
                      {s.batch_selection_remarks && <div className="text-[11px] text-ink-muted">{s.batch_selection_remarks}</div>}
                    </TableCell>
                    <TableCell className="text-xs">{s.created_by ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <StatCard>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-1 text-lg font-bold tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 text-[10.5px] text-ink-muted">{hint}</div>}
    </StatCard>
  );
}
